import { getRemoteConfig, getValue, fetchAndActivate } from 'firebase/remote-config';
import { app } from '../config/firebase';


// Default values — Firebase serves these when a fetch fails, and we also
// hand them to Firebase as defaultConfig below.
const DEFAULT_CONFIG = {
    "dashboard_access_config": JSON.stringify({
        // XSite — core product, visible to everyone (Step 1 of the dashboard flow).
        "xsite-builder": { "admin": true, "user": true },
        "past-sites": { "admin": true, "user": true },
        "past-site-jobs": { "admin": true, "user": true },
        "customers": { "admin": true, "user": true },
        // Project Management
        "projects": { "admin": true, "user": true },
        "keywordsgroup": { "admin": true, "user": true },
        "keywords": { "admin": true, "user": false },
        "templates": { "admin": true, "user": false }
    }),
    // Master switch for the subscription-plans experience. true → full plan UI
    // (tiers, upgrade/downgrade, domain limits). false → credits-only (packs UI,
    // no plan name, no domain limits).
    //
    // Default FLIPPED to false on 2026-09-03. Plans are retired: the domain-limit
    // enforcement behind them was deactivating users' domains on production, and
    // /dashboard/plans is still reachable from about ten places (profile, dashboard,
    // XSite "buy credits", job details, import). With the old default of "true",
    // every failure mode showed Plans & Pricing — config not initialised, an empty
    // value, or a throw — so a retired purchase flow was what users saw whenever
    // Remote Config was slow or unreachable. A retired feature must fail HIDDEN.
    "plans_enabled": "false",
    // Customers / Reservations dashboard. Default OFF — it runs on mock data,
    // so it must stay hidden in production until the real feed is wired. Turn
    // ON per environment (e.g. test) to demo it.
    "customers_enabled": "false",
    // XSite site-creation: max ADDITIONAL languages (beyond the primary) a user
    // may pick during creation. Admin-tunable per environment via Remote Config.
    // Default "5" — unchanged from the previously hardcoded cap.
    "xsite_max_additional_languages": "5",
    // XSite site-creation: max PAGES a user may have on the site (Design step).
    // Admin-tunable per environment via Remote Config. Default "10".
    "xsite_max_pages": "10",
    // Get found v3: the live Business Profile lane's uid allowlist (see above).
    // NOTE: xsite_max_unhosted_sites was retired — the cap now comes from the
    // backend's public settings endpoint (GET /xeo/v1/config → max_unhosted_sites),
    // the single source of truth shared with admin PATCH /xeo/admin/settings.
    // See src/services/publicConfig.js.
    "dashboard_menu_structure": JSON.stringify({
        "XWatch": { "domains": { "label": "Domains", "enabled": true } },
        "XWrite": {
            "article-writer": { "label": "AI Writer", "enabled": true },
            "past-articles": { "label": "Past Articles", "enabled": true },
            "past-writing-jobs": { "label": "Past Writing Jobs", "enabled": true }
        },
        "XSite": {
            "projects": { "label": "Projects", "enabled": true },
            "keywordsgroup": { "label": "Keyword Groups", "enabled": true },
            "keywords": { "label": "Keywords", "enabled": true },
            "templates": { "label": "Templates", "enabled": true }
        }
    })
};

// Firebase Remote Config hard-depends on IndexedDB. In private/incognito
// mode, with corrupted browser storage, or under strict storage
// partitioning (common on Safari), `indexedDB.open` fails and RC throws
// `remoteconfig/storage-open` on every init AND every getValue — flooding
// the console even though the app degrades to defaults perfectly well.
//
// So we probe IndexedDB once. If it's not usable we never touch Firebase RC
// (no instance, no fetch, no getValue) and let callers fall back to their
// existing defaults — identical behavior to before, minus the error spam.
let remoteConfig = null;   // lazily created, only when IndexedDB works
let initPromise = null;    // dedupe concurrent init() calls (useAccess + Dashboard both call it)

const indexedDBUsable = () => new Promise((resolve) => {
    try {
        if (typeof indexedDB === 'undefined' || !indexedDB) return resolve(false);
        const req = indexedDB.open('__rc_probe__');
        req.onsuccess = () => {
            try {
                req.result.close();
                indexedDB.deleteDatabase('__rc_probe__');
            } catch (_) { /* best-effort cleanup */ }
            resolve(true);
        };
        req.onerror = () => resolve(false);
        req.onblocked = () => resolve(false);
    } catch (_) {
        resolve(false);
    }
});

const doInitialize = async () => {
    if (!(await indexedDBUsable())) {
        console.info('[remoteConfig] IndexedDB unavailable — skipping Firebase Remote Config, using built-in defaults');
        return false;
    }
    try {
        remoteConfig = getRemoteConfig(app);
        remoteConfig.settings.minimumFetchIntervalMillis = 0; // immediate fetch (debug setting — see note)
        // The dashboard shell and route guards wait on this fetch before rendering,
        // so this is how long a page can stay blank. When Firebase throttles the
        // fetch (429) the SDK waits out its backoff until this timeout fires —
        // at 60s that was a minute of empty shell. A normal fetch takes ~300ms;
        // past 8s we fall back exactly as for any other failed fetch.
        remoteConfig.settings.fetchTimeoutMillis = 8000;
        remoteConfig.defaultConfig = DEFAULT_CONFIG;
        await fetchAndActivate(remoteConfig);
        return true;
    } catch (error) {
        // Any unexpected RC failure → drop the instance and fall back to
        // defaults, same as the IndexedDB-unavailable path.
        remoteConfig = null;
        console.warn('[remoteConfig] init failed, using defaults:', error?.message || error);
        return false;
    }
};

export const initializeRemoteConfig = () => {
    if (!initPromise) initPromise = doInitialize();
    return initPromise;
};

// Boolean reader for the plans master-switch. Reads the raw RC string and
// coerces common falsy spellings (false/no/0/off/disabled) → false. Defaults
// to TRUE whenever RC is unavailable, unset, or unparseable, so the plan
// experience stays on exactly as today unless an admin explicitly turns it off.
export const getPlansEnabled = () => {
    // Every fallback now returns FALSE. All four used to return true — no config
    // object, an empty string, a throw, and the baked-in default — which meant the
    // page could only fail towards showing a retired purchase flow. Turning plans
    // back on is now a deliberate act, not what happens when something breaks.
    if (!remoteConfig) return false;
    try {
        const raw = String(getValue(remoteConfig, 'plans_enabled')._value).trim().toLowerCase();
        if (raw === '') return false;
        return ['true', 'yes', '1', 'on', 'enabled'].includes(raw);
    } catch (error) {
        return false;
    }
};

// Customers/Reservations dashboard switch. Defaults to FALSE (hidden) whenever
// RC is unavailable, unset, or unparseable — mock-data feature stays off in
// production unless an admin explicitly enables it.
export const getCustomersEnabled = () => {
    if (!remoteConfig) return false;
    try {
        const raw = String(getValue(remoteConfig, 'customers_enabled')._value).trim().toLowerCase();
        return ['true', 'yes', '1', 'on', 'enabled'].includes(raw);
    } catch (error) {
        return false;
    }
};

// XSite max additional languages (site creation). Numeric reader — parses the RC
// string to a non-negative int, falls back to 5 (the historical cap) whenever RC
// is unavailable/unset/unparseable, and clamps to a sane ceiling so a fat-finger
// admin value can't blow up the language picker.
export const getXSiteMaxAdditionalLanguages = () => {
    const DEFAULT = 5;
    if (!remoteConfig) return DEFAULT;
    try {
        const raw = String(getValue(remoteConfig, 'xsite_max_additional_languages')._value).trim();
        if (raw === '') return DEFAULT;
        const n = parseInt(raw, 10);
        if (!Number.isFinite(n) || n < 0) return DEFAULT;
        return Math.min(n, 50);
    } catch (error) {
        return DEFAULT;
    }
};

// Max PAGES a user may have on an XSite (Design step). Mirrors the additional-
// languages cap: falls back to the default when RC is unavailable/unset/unparseable
// and clamps to a sane ceiling so a fat-finger admin value can't blow up the UI.
export const getXSiteMaxPages = () => {
    const DEFAULT = 10;
    if (!remoteConfig) return DEFAULT;
    try {
        const raw = String(getValue(remoteConfig, 'xsite_max_pages')._value).trim();
        if (raw === '') return DEFAULT;
        const n = parseInt(raw, 10);
        if (!Number.isFinite(n) || n < 1) return DEFAULT;
        return Math.min(n, 100);
    } catch (error) {
        return DEFAULT;
    }
};

// getXSiteMaxUnhostedSites was removed — the unhosted-site cap now comes from the
// backend public settings endpoint (GET /xeo/v1/config). See src/services/publicConfig.js.

export const getRemoteConfigValue = (key) => {
    // No usable RC instance (IndexedDB unavailable, init not finished, or
    // init failed) → return null so callers use their own fallback, exactly
    // as before. We deliberately don't serve DEFAULT_CONFIG here: that would
    // change access/menu behavior for broken-IndexedDB users (a separate
    // decision), whereas null preserves the current contract.
    if (!remoteConfig) return null;
    try {
        const value = getValue(remoteConfig, key);
        return JSON.parse(value._value);
    } catch (error) {
        return null;
    }
};
